import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Form } from '@unform/web';
import { includes, isArray } from 'lodash';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import { Spinner } from '~/components';
import {
  Input,
  Combobox,
  DateTime,
  Autocomplete,
  Select,
  Hidden,
} from '~/components/Form';
import {
  CENTRO_CUSTO_ABASTECIMENTO,
  MOTORISTA_ABASTECIMENTO,
  TRR_CHILE,
  CUSTOMIZACAO_RAIZEN,
  MODULO_CUSTOMIZACAO_RUMO,
} from '~/constants/modules';
import { toast } from 'react-toastify';
import qs from 'querystring';
import { t, can, validCharacters } from '~/helpers';
import api from '~/services/api';
import { Container } from './styles';

export default function CreateOrUpdate({
  data,
  reference,
  close,
  cancelarAcao,
}) {
  const { client } = useSelector((state) => state.user.profile);
  const [posto, setPosto] = useState(0);
  const [motorista, setMotorista] = useState(null);
  const [totalizador, setTotalizador] = useState(0);
  const [custo, setCusto] = useState(null);
  const [compartments, setCompartments] = useState([]);

  const {
    internounidatausuario,
    formaidfrentistaabast,
    exibeoslubrificacao,
  } = useSelector((state) => state.user.configs);

  const [loading, setLoading] = useState(true);
  const [initials, setInitials] = useState(null);
  const [idatendimento, setIdatendimento] = React.useState(null);
  const [
    idatendimentolubrificacao,
    setIdatendimentolubrificacao,
  ] = React.useState(null);
  const [idposto, setIdposto] = React.useState(null);
  const [idmovimentacao, setIdmovimentacao] = React.useState(null);
  const [observacaolubrificacao, setObservacaolubrificacao] = React.useState(
    null
  );

  const [idusuario, setIdusuario] = React.useState(null);
  const [codigousuarioRegistro, setCodigousuarioRegistro] = React.useState(
    null
  );

  const modules = useSelector((state) => state.user.modules);
  const clienteRumo = modules.includes(MODULO_CUSTOMIZACAO_RUMO);

  const [atendeugovernador, setAtendeugovernador] = useState('N');
  const [atendeugerador, setAtendeugerador] = useState('N');
  const [atendeucompressor, setAtendeucompressor] = useState('N');

  const [volumegovernador, setVolumegovernador] = useState(0);
  const [volumegerador, setVolumegerador] = useState(0);
  const [volumecompressor, setVolumecompressor] = useState(0);

  const optionsOk = useMemo(
    () => [
      { value: 'S', label: t('s.geral.opcoes.ok') },
      { value: 'N', label: t('s.geral.opcoes.nok') },
    ],
    []
  );

  const cellsUser = useMemo(
    () => [
      {
        id: 'codigousuario',
        label: t('s.cadastro.usuario.codigousuario'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeusuario',
        label: t('s.cadastro.usuario.nomeusuario'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  useEffect(() => {
    if (initials) {
      setPosto(initials.idposto);
      setTotalizador(Number(initials.tipototalizadorveiculo));
      setLoading(false);
    }
  }, [initials]);

  useEffect(() => {
    async function carregarDados() {
      if (!data) return;

      const {
        idmovimentacao,
        idatendimento,
        idposto,
        idveiculo,
        idproduto,
      } = data;

      try {
        // chamada
        const urlAtendimento = idmovimentacao
          ? `pitstop/lubrificacao/movimentacao/${idmovimentacao}/${idposto}/${idatendimento}`
          : `pitstop/lubrificacao/${idatendimento}/${idposto}`;

        const resAtendimento = await api.get(urlAtendimento);
        const atendimento = resAtendimento.data;

        // pega idatendimento REAL da API (se vier atualizado)
        const idAtendimentoFinal = atendimento.idatendimento || idatendimento;

        // chamada (AGORA SIM depende da 1ª)
        let lubrificacao = {};
        if (idAtendimentoFinal) {
          const resLubrificacao = await api.get(
            `/pitstop/lubrificacao/${idAtendimentoFinal}`
          );
          lubrificacao = resLubrificacao.data;
        }

        // chamada (independente → pode ser depois)
        const resCompartimentos = await api.get('veiculoscompartimentos', {
          params: {
            wherefield: ['idveiculo', 'idproduto'],
            typewherefield: ['EQUALS', 'EQUALS'],
            valuewherefield: [idveiculo, idproduto],
            pesquisalista: true,
          },
          paramsSerializer: (param) => qs.stringify(param),
        });

        // console.log("Atendimento:", atendimento);
        // console.log("Lubrificação:", lubrificacao);

        // 🔹 states normais
        setIdmovimentacao(idmovimentacao);
        setIdatendimento(idAtendimentoFinal);
        setIdposto(atendimento.idposto);
        setIdusuario(atendimento.idusuario);
        setCodigousuarioRegistro(atendimento.codigousuario);

        setIdatendimentolubrificacao(lubrificacao.idatendimentolubrificacao);
        setObservacaolubrificacao(lubrificacao.observacaolubrificacao);

        setAtendeugovernador(
          lubrificacao.atendeugovernadorlubrificacao
            ? lubrificacao.atendeugovernadorlubrificacao
            : 'N'
        );
        setAtendeugerador(
          lubrificacao.atendeugeradorlubrificacao
            ? lubrificacao.atendeugeradorlubrificacao
            : 'N'
        );
        setAtendeucompressor(
          lubrificacao.atendeucompressorlubrificacao
            ? lubrificacao.atendeucompressorlubrificacao
            : 'N'
        );

        // UM ÚNICO setInitials
        setInitials({
          ...atendimento,
          atendeugovernador: lubrificacao.atendeugovernadorlubrificacao || 'N',
          atendeugerador: lubrificacao.atendeugeradorlubrificacao || 'N',
          atendeucompressor: lubrificacao.atendeucompressorlubrificacao || 'N',
          volumegovernador: lubrificacao.volumegovernadorlubrificacao
            ? lubrificacao.volumegovernadorlubrificacao
            : 0,
          volumegerador: lubrificacao.volumegeradorlubrificacao
            ? lubrificacao.volumegeradorlubrificacao
            : 0,
          volumecompressor: lubrificacao.volumecompressorlubrificacao
            ? lubrificacao.volumecompressorlubrificacao
            : 0,
        });

        // Compartimentos
        if (resCompartimentos.data.items) {
          setCompartments(
            resCompartimentos.data.items.map((i) => ({
              label: `${i.idcompartimento} - ${i.descricaocompartimento}`,
              value: i.idcompartimento,
            }))
          );
        }
      } catch (err) {
        // erro quando datas incompativeis
        if (err && isArray(err)) {
          toast.error(err[0]);
          await close();
        }
      }
    }

    carregarDados();
  }, [data]);

  async function handleCloseModal() {
    await this.setState({ open: false });
  }

  async function hancleCancelarAcao() {
    await this.setState((prevState) => ({
      ...prevState,
    }));
  }

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        observacaolubrificacao: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      // console.log('param', param);

      const atendeugovernadorlubrificacao = param.atendeugovernador
        ? param.atendeugovernador
        : null;

      const atendeugeradorlubrificacao = param.atendeugerador
        ? param.atendeugerador
        : null;

      const atendeucompressorlubrificacao = param.atendeucompressor
        ? param.atendeucompressor
        : null;

      const volumegovernadorlubrificacao = param.volumegovernador
        ? param.volumegovernador
        : 0.0;

      const volumegeradorlubrificacao = param.volumegerador
        ? param.volumegerador
        : 0.0;

      const volumecompressorlubrificacao = param.volumecompressor
        ? param.volumecompressor
        : 0.0;

      const objSend = {
        idatendimentolubrificacao: idatendimentolubrificacao,
        idatendimento: idatendimento,
        idcliente: client,
        idposto: idposto,
        idmovimentacao: idmovimentacao,
        atendeuatendimentolubrificacao: 'S',
        idusuariolibintegraatendimentolubrificacao:
          param?.idusuario !== undefined ? param.idusuario : idusuario,
        codigousuario:
          param?.codigousuario !== undefined
            ? param.codigousuario
            : codigousuarioRegistro,
        observacaolubrificacao: param.observacaolubrificacao,
        atendeugovernadorlubrificacao,
        atendeugeradorlubrificacao,
        atendeucompressorlubrificacao,
        volumegovernadorlubrificacao,
        volumegeradorlubrificacao,
        volumecompressorlubrificacao,
      };
      await api.put('pitstop/lubrificacao', objSend);
      await close();
      toast.success(t('s.geral.sucessoalteracao'));
    } catch (err) {
      if (err && isArray(err)) {
        toast.error(err[0]);
      }
      await cancelarAcao();
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  async function handleAtendeugovernador(value) {
    await reference.current.setFieldValue('atendeugovernador', value);
    await setAtendeugovernador(value);
  }

  async function handleAtendeugerador(value) {
    await reference.current.setFieldValue('atendeugerador', value);
    await setAtendeugerador(value);
  }

  async function handleAtendeucompressor(value) {
    await reference.current.setFieldValue('atendeucompressor', value);
    await setAtendeucompressor(value);
  }

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Container>
            <Autocomplete
              name="idposto"
              disabled
              disableClearable={true}
              label="codigonomeposto"
              url="postoscomboios"
              unique="idposto"
              title={t('s.menu.configuracao.posto')}
            />

            <Combobox
              name="idbico"
              disabled
              label="bicoprodutobico"
              url="bicos"
              unique="idbico"
              title={t('s.menu.configuracao.bico')}
              hiddens={[{ name: 'idterminal' }, { name: 'idproduto' }]}
              params={{
                wherefield: ['idposto', 'tipoproduto'],
                valuewherefield: [posto.idposto, 'L'],
                typewherefield: ['EQUALS', 'EQUALS'],
                fieldlist: 'idbico, idterminal, idproduto, bicoprodutobico',
              }}
            />

            <DateTime
              time
              name="datahorainicialmovimentacao"
              disabled
              label={t('s.operacao.abastecimento.datahorainicialmovimentacao')}
            />

            <DateTime
              time
              name="datahorafinalmovimentacao"
              disabled
              label={t('s.operacao.abastecimento.datahorafinalmovimentacao')}
              style={{ marginTop: 16 }}
            />

            <Input
              name="idveiculo"
              disabled
              label={
                !can(TRR_CHILE)
                  ? t('s.operacao.abastecimento.idveiculo')
                  : t('s.cadastro.codigoempresacliente')
              }
              style={{ marginTop: 16 }}
            />

            <Autocomplete
              name="idempresaveiculo"
              label={
                can(CUSTOMIZACAO_RAIZEN)
                  ? 'idempresaveiculo'
                  : 'nomeempresaveiculo'
              }
              url="empresasveiculos"
              unique="idempresaveiculo"
              title={t('s.cadastro.veiculo.idempresaveiculo')}
              disabled={true}
            />

            {can(CENTRO_CUSTO_ABASTECIMENTO) && (
              <Input
                name="idcentrocusto"
                disabled
                label={t('s.operacao.abastecimento.idcentrocusto')}
                value={custo}
              />
            )}

            <Input
              name="totalizadormovimentacaoabastecimento"
              type="number"
              disabled
              label={t(
                `s.operacao.abastecimento.${
                  totalizador === 1 ? 'horimetro' : 'hodometro'
                }`
              )}
              style={{
                display: includes([1, 2], totalizador) ? 'block' : 'none',
              }}
            />

            <Input
              name="encerranteinicialmovimentacao"
              disabled
              label={t(
                's.operacao.abastecimento.encerranteinicialmovimentacao'
              )}
              type="number"
            />

            <Input
              name="encerrantefinalmovimentacao"
              disabled
              label={t('s.operacao.abastecimento.encerrantefinalmovimentacao')}
              type="number"
            />

            <Input
              name="volumemovimentacao"
              disabled
              label={t('s.operacao.abastecimento.volumemovimentacao')}
              type="number"
            />

            {formaidfrentistaabast !== 0 && (
              <Autocomplete
                name="codigousuario"
                label="nomecodigousuario"
                unique="codigousuario"
                title={t('s.operacao.abastecimento.codigousuario')}
                url="usuarios"
                disableClearable={true}
                hiddens={[{ name: 'idusuario' }]}
                params={{
                  filterusrposto: idposto,
                  edicao: Boolean(initials?.idatendimentolubrificacao),
                }}
                disabled={Boolean(
                  initials?.idusuario || initials?.codigousuario
                )}
              />
            )}

            <Hidden name="idusuario" devaultValue={idusuario} />

            <Hidden
              name="codigousuarioRegistro"
              devaultValue={codigousuarioRegistro}
            />

            {can(MOTORISTA_ABASTECIMENTO) && (
              <Input
                name="idmotorista"
                disabled
                label={t('s.operacao.abastecimento.idmotorista')}
                value={motorista}
              />
            )}

            <Select
              name="idcompartimento"
              label={t('s.operacao.lubrificacao.idcompartimento')}
              options={compartments}
              disabled
            />

            <Select
              name="trocalubmovimentacaolubrificacao"
              label={t(
                's.operacao.lubrificacao.trocalubmovimentacaolubrificacao'
              )}
              options={[
                { label: t('s.geral.opcoes.nao'), value: 0 },
                { label: t('s.geral.opcoes.sim'), value: 1 },
              ]}
              disabled
            />

            {exibeoslubrificacao === 1 && (
              <Input
                name="numeroosmovimentacaolubrificacao"
                label={t(
                  's.operacao.lubrificacao.numeroosmovimentacaolubrificacao'
                )}
              />
            )}

            {clienteRumo === false ? (
              <>
                <Select
                  name="atendeugovernador"
                  id="atendeugovernador"
                  label={t('s.cadastro.pitstop.atendeugovernadorlubrificacao')}
                  options={optionsOk}
                  onChange={({ target }) =>
                    handleAtendeugovernador(target.value)
                  }
                  // defaultValue={antifurtoabastecimento}
                  style={{ marginTop: 10, width: '100%' }}
                  // getValue={(value) => setAntifurtoabastecimento(value)}
                />

                {atendeugovernador === 'S' ? (
                  <Input
                    name="volumegovernador"
                    label={t('s.cadastro.pitstop.volumegovernadorlubrificacao')}
                    type="number"
                    defaultValue={initials.volumegovernador || 0}
                    style={{ marginTop: 0, marginBottom: 10, width: '100%' }}
                  />
                ) : (
                  <Hidden name="volumegovernador" value="0" />
                )}

                <Select
                  name="atendeugerador"
                  id="atendeugerador"
                  label={t('s.cadastro.pitstop.atendeugeradorlubrificacao')}
                  options={optionsOk}
                  onChange={({ target }) => handleAtendeugerador(target.value)}
                  // defaultValue={chipabastecimento}
                  style={{ marginTop: 6, width: '100%' }}
                  // getValue={(value) => setChipabastecimento(value)}
                />

                {atendeugerador === 'S' ? (
                  <Input
                    name="volumegerador"
                    label={t('s.cadastro.pitstop.volumegeradorlubrificacao')}
                    type="number"
                    defaultValue={initials.volumegerador || 0}
                    style={{ marginTop: 0, marginBottom: 10, width: '100%' }}
                  />
                ) : (
                  <Hidden name="volumegerador" value="0" />
                )}

                <Select
                  name="atendeucompressor"
                  id="atendeucompressor"
                  label={t('s.cadastro.pitstop.atendeucompressorlubrificacao')}
                  options={optionsOk}
                  onChange={({ target }) =>
                    handleAtendeucompressor(target.value)
                  }
                  // defaultValue={visorabastecimento}
                  style={{ marginTop: 6, width: '100%', marginBottom: 4 }}
                  // getValue={(value) => setVisorabastecimento(value)}
                />

                {atendeucompressor === 'S' ? (
                  <Input
                    name="volumecompressor"
                    label={t('s.cadastro.pitstop.volumecompressorlubrificacao')}
                    type="number"
                    defaultValue={initials.volumecompressor || 0}
                    style={{ marginTop: 0, width: '100%', marginBottom: 24 }}
                  />
                ) : (
                  <Hidden name="volumecompressor" value="0" />
                )}
              </>
            ) : (
              <>
                <Hidden name="atendeugovernador" value="" />
                <Hidden name="atendeugerador" value="" />
                <Hidden name="atendeucompressor" value="" />
                <Hidden name="volumegovernador" value="0" />
                <Hidden name="volumegerador" value="0" />
                <Hidden name="volumecompressor" value="0" />
              </>
            )}

            <Input
              name="observacaolubrificacao"
              label={t('s.cadastro.pitstop.observacaoatendimento')}
              defaultValue={observacaolubrificacao}
              multiline
              rows={3}
              inputProps={{ maxLength: 500 }}
              style={{ marginTop: 4 }}
            />

            <Hidden
              name="idatendimentolubrificacao"
              devaultValue={idatendimentolubrificacao}
            />
          </Container>
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
